import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { useScrollReveal } from "../hooks/useScrollReveal";
import usePageSeo from "../hooks/usePageSeo";
import "./Contact.css";

const CONTACT_BG = "/images/hero/hero.png";
const NETLIFY_FORM_NAME = "contact";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_LENGTH = { name: 80, surname: 80, email: 120, phone: 20, message: 2000 };
const SUBMIT_COOLDOWN_MS = 30000;

function sanitize(str) {
  return str.replace(/[<>]/g, "").trim();
}

function encodeFormData(data) {
  return new URLSearchParams(data).toString();
}

function validate(form) {
  if (!form.name.trim() || form.name.length > MAX_LENGTH.name) return false;
  if (form.surname.length > MAX_LENGTH.surname) return false;
  if (!EMAIL_REGEX.test(form.email) || form.email.length > MAX_LENGTH.email) return false;
  if (!form.message.trim() || form.message.length > MAX_LENGTH.message) return false;
  return true;
}

export default function Contact() {
  useScrollReveal();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const lastSubmitRef = useRef(0);

  const [form, setForm] = useState({ name: "", surname: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState(null);

  usePageSeo({
    lang,
    path: "/contact",
    title: lang === "IT"
      ? "Contatti | Richiedi preventivo a GDM Installations"
      : "Contact | Request a quote from GDM Installations",
    description: lang === "IT"
      ? "Contatta GDM Installations per preventivi, consulenze e richieste su porte interne, montaggio arredi e lavorazioni in legno su misura."
      : "Contact GDM Installations for quotes, consultations and requests about interior doors, furniture assembly and custom woodwork.",
    keywords: lang === "IT"
      ? "contatti GDM Installations, preventivo falegnameria Bari, richiesta montaggio arredi, contatto porte interne Bari"
      : "contact GDM Installations, carpentry quote Bari, furniture assembly request, interior doors contact Bari",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: lang === "IT" ? "Contatti GDM Installations" : "GDM Installations Contact",
      url: "https://gdminstallations.it/contact",
      description: lang === "IT"
        ? "Pagina contatti per richieste di preventivo e consulenza."
        : "Contact page for quote requests and consultations.",
      mainEntity: {
        "@type": "LocalBusiness",
        name: "GDM Installations",
        areaServed: "Bari e dintorni",
      },
    },
  });

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    const maxLen = MAX_LENGTH[name] || 200;
    setForm((prev) => ({ ...prev, [name]: value.slice(0, maxLen) }));
  }, []);

  const handleSubmit = async () => {
    const now = Date.now();
    if (now - lastSubmitRef.current < SUBMIT_COOLDOWN_MS) return;
    if (!validate(form)) return;

    setStatus("loading");
    lastSubmitRef.current = now;

    try {
      const payload = {
        "form-name": NETLIFY_FORM_NAME,
        name: sanitize(form.name),
        surname: sanitize(form.surname),
        email: sanitize(form.email),
        phone: sanitize(form.phone),
        message: sanitize(form.message),
      };

      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encodeFormData(payload),
      });

      if (!response.ok) {
        throw new Error("Netlify form submission failed");
      }

      setStatus("success");
      setForm({ name: "", surname: "", email: "", phone: "", message: "" });
    } catch {
      setStatus("error");
    }
  };

  const handleCancel = () => {
    setForm({ name: "", surname: "", email: "", phone: "", message: "" });
    setStatus(null);
    navigate("/");
  };

  return (
    <div className="contact-page">
      <div className="page-hero-mini">
        <h1 className="page-hero-title fade-in">{t.contact.title}</h1>
        <div className="gold-line" />
        <p className="page-hero-sub fade-in fade-in-delay-1">{t.contact.subtitle}</p>
      </div>

      <section className="contact-hero">
        <div className="contact-hero-bg" style={{ backgroundImage: `url(${CONTACT_BG})` }} />
        <div className="contact-hero-overlay" />
        <div className="contact-hero-inner">
          <div className="contact-card fade-in" ref={formRef}>
            <div className="contact-card-header">
            </div>
            <form
              className="contact-form"
              name={NETLIFY_FORM_NAME}
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <input type="hidden" name="form-name" value={NETLIFY_FORM_NAME} />
              <p style={{ display: "none" }} aria-hidden="true">
                <label htmlFor="contact-bot-field">Do not fill this field</label>
                <input id="contact-bot-field" type="text" name="bot-field" tabIndex="-1" autoComplete="off" />
              </p>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="contact-name">{t.contact.name}</label>
                  <input id="contact-name" type="text" name="name" autoComplete="given-name"
                    placeholder={t.contact.namePlaceholder} value={form.name} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="contact-surname">{t.contact.surname}</label>
                  <input id="contact-surname" type="text" name="surname" autoComplete="family-name"
                    placeholder={t.contact.surnamePlaceholder} value={form.surname} onChange={handleChange} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="contact-email">{t.contact.email}</label>
                  <input id="contact-email" type="email" name="email" autoComplete="email"
                    placeholder={t.contact.emailPlaceholder} value={form.email} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="contact-phone">{t.contact.phone || "Telefono"}</label>
                  <input id="contact-phone" type="tel" name="phone" autoComplete="tel"
                    placeholder={t.contact.phonePlaceholder || "Il tuo numero"} value={form.phone} onChange={handleChange} />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="contact-message">{t.contact.message}</label>
                <textarea id="contact-message" name="message" rows={4}
                  placeholder={t.contact.messagePlaceholder} value={form.message} onChange={handleChange} />
              </div>

              {status === "success" && <p className="form-status success" role="alert">{t.contact.successMsg}</p>}
              {status === "error"   && <p className="form-status error"   role="alert">{t.contact.errorMsg}</p>}

              <div className="form-actions">
                <button type="button" className="btn-outline" onClick={handleCancel} disabled={status === "loading"}>{t.contact.cancel}</button>
                <button type="submit" className="btn-gold" disabled={status === "loading" || !validate(form)}>
                  {status === "loading" ? "..." : t.contact.confirm}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
